import CustomDialog from 'common/components/CustomDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { formatDate } from 'common/utils/date'
import endOfToday from 'date-fns/endOfToday'
import { useMutateEndCycle, useMutateRemoveCycle } from 'modules/pond/api/mutation'
import { useGetCycles } from 'modules/pond/api/queries'
import { useGetPondNameById } from 'modules/pond/api/selector'
import PondCycleForm from 'modules/pond/components/PondCycleForm'
import SettingTable from 'modules/pond/components/SettingTable'
import { isSettingEditState, selectedCycleIdState } from 'modules/pond/stores/atom'
import { useFirstAllowedPondPath, usePermitToActionFeature } from 'modules/user/api/selector'
import { ChangeEvent, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import WarningAmberIcon from '@mui/icons-material/InfoOutlined'
import LoadingButton from '@mui/lab/LoadingButton'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import './style.css'

enum Mode {
  EndCycle = 'endCycle',
  RemoveCycle = 'removeCycle',
}

const END_CYCLE_CONFIRM_TXT = 'จบรอบการเลี้ยง'
const REMOVE_CYCLE_CONFIRM_TXT = 'ลบรอบการเลี้ยง'

const SettingPage = () => {
  const { t } = useTranslation(['common', 'pond', 'pondComparison', 'auth'])
  const { pondId } = useParams()

  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [openEdit, setOpenEdit] = useRecoilState(isSettingEditState)
  const [value, setValue] = useState('')
  const [mode, setMode] = useState(Mode.EndCycle)
  const [endDate, setEndDate] = useState(new Date())

  const [cycleId, setCycleId] = useRecoilState(selectedCycleIdState)

  const endCycleMutation = useMutateEndCycle()
  const removeCycleMutation = useMutateRemoveCycle()
  const showUpdateCycleBtn = usePermitToActionFeature([CRUDAction.Update], FeatureKey.Cycle)
  const showDeleteCycleBtn = usePermitToActionFeature([CRUDAction.Delete], FeatureKey.Cycle)

  const { data: pond } = useGetPondNameById(pondId || '')
  const firstAllowedPondPath = useFirstAllowedPondPath(pond?.farmId || '', pond?.id || '')
  const theme = useTheme()

  const { data: cycles, isLoading: isCycleLoading } = useGetCycles()

  useEffect(() => {
    return () => {
      setOpenEdit(false)
    }
  }, [])

  const handleCloseDialog = () => {
    setOpen(false)
  }

  const handleOpenDialog = (mode: Mode) => {
    setMode(mode)
    setValue('')
    setOpen(true)
  }

  const handleRemoveCycle = async () => {
    await removeCycleMutation.mutateAsync(undefined, {
      onSuccess: async () => {
        setCycleId(null)
        handleCloseDialog()
        navigate(firstAllowedPondPath)
      },
    })
  }

  const handleEndCycle = async () => {
    await endCycleMutation.mutateAsync(formatDate(endDate), {
      onSuccess: () => {
        handleCloseDialog()
        navigate(firstAllowedPondPath)
      },
    })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setValue(value)
  }

  const dialogContent = {
    [Mode.EndCycle]: {
      title: t('pond:end.pond.cycle'),
      content: t('pond:confirm.end.pond.cycle'),
      confirmTxt: END_CYCLE_CONFIRM_TXT,
      onSubmit: handleEndCycle,
      confirmBtn: t('pond:confirm.end.pond.cycle.btn'),
    },
    [Mode.RemoveCycle]: {
      title: t('pond:delete.pond.cycle'),
      content: t('pond:remove.pond.cycle.content'),
      confirmTxt: REMOVE_CYCLE_CONFIRM_TXT,
      onSubmit: handleRemoveCycle,
      confirmBtn: t('pond:remove.pond.cycle.btn'),
    },
  }

  const handleChangeDate = (
    value: Date | null,
    _context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    if (!value) return
    setEndDate(value)
  }

  const [isDirty, setIsDirty] = useState(false)

  const updateCycleInfo = (id: string) => {
    if (!cycles) return

    setCycleId(id)
  }

  return (
    <>
      {!openEdit ? (
        <SettingTable
          cycles={cycles ?? []}
          isLoading={isCycleLoading}
          showUpdateCycleBtn={showUpdateCycleBtn}
          showDeleteCycleBtn={showDeleteCycleBtn}
          onEdit={(id) => {
            updateCycleInfo(id)
            setOpenEdit(true)
          }}
          onEnd={(id) => {
            updateCycleInfo(id)
            handleOpenDialog(Mode.EndCycle)
          }}
          onDelete={(id) => {
            updateCycleInfo(id)
            handleOpenDialog(Mode.RemoveCycle)
          }}
        />
      ) : (
        <>
          {openEdit && !isCycleLoading && pond && (
            <Stack
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography variant="h4">{`${t('common:edit')}${t(
                'pondComparison:cycleLabel'
              )}`}</Typography>
              <Stack
                className="overview-dashboard-datepicker"
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{ position: 'relative' }}
              >
                <Button
                  onClick={() => {
                    setOpenEdit(false)
                  }}
                  variant="outlined"
                  color="inherit"
                  sx={{
                    borderRadius: 1,
                    fontSize: 16,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: 'white',
                  }}
                >
                  {t('common:back')}
                </Button>

                <LoadingButton
                  disabled={!isDirty}
                  variant="contained"
                  color="primary"
                  type="submit"
                  form="setting-pond"
                  sx={{ borderRadius: 1, fontSize: 16, fontWeight: 500 }}
                >
                  {t('common:save')}
                </LoadingButton>
              </Stack>
            </Stack>
          )}
          <Box sx={{ pb: 3, position: 'relative' }}>
            <Card sx={{ p: 3, width: { xs: '100%', md: '50%' } }}>
              <Box py={2}>
                <PondCycleForm
                  formId="setting-pond"
                  pondId={pondId || ''}
                  onDirtyChange={setIsDirty}
                  showSaveButton={false}
                />
              </Box>
            </Card>
          </Box>
        </>
      )}
      <CustomDialog
        className="setting-dialog"
        disabledAction
        title={dialogContent[mode].title}
        closeDialog={handleCloseDialog}
        open={open}
        titleIcon={<WarningAmberIcon sx={{ color: theme.palette.warning.main }} />}
      >
        <Stack spacing={2}>
          <Typography>
            <Trans
              i18nKey={dialogContent[mode].content}
              components={{ b: <Typography component="span" variant="h5" color="error" /> }}
            />
          </Typography>
          <Stack spacing={2}>
            <TextField
              id="end-confirm"
              value={value}
              onChange={handleChange}
              size="small"
              placeholder={dialogContent[mode].confirmTxt}
            />
            {mode === Mode.EndCycle && (
              <Stack spacing={1}>
                <label htmlFor="cycle-end-date-picker">
                  {' '}
                  <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                    *
                  </Typography>
                  {t('pond:end.cycle.date')}
                </label>
                <DatePicker
                  value={endDate}
                  onChange={handleChangeDate}
                  maxDate={endOfToday()}
                  slotProps={{
                    textField: {
                      id: 'cycle-end-date-picker',
                      size: 'small',
                      sx: { width: '100%' },
                    },
                  }}
                />
              </Stack>
            )}
          </Stack>
          <Stack
            className="overview-dashboard-datepicker"
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 1, position: 'relative' }}
          >
            <Button
              onClick={() => {
                setOpen(false)
              }}
              variant="outlined"
              color="inherit"
              sx={{
                width: '100%',
                borderRadius: 1,
                fontSize: 16,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'white',
              }}
            >
              {t('common:cancel')}
            </Button>

            <Button
              disabled={dialogContent[mode].confirmTxt !== value}
              variant="contained"
              color="primary"
              type="submit"
              sx={{ width: '100%', borderRadius: 1, fontSize: 16, fontWeight: 500 }}
              onClick={dialogContent[mode].onSubmit}
            >
              {t('auth:confirm')}
            </Button>
          </Stack>
        </Stack>
      </CustomDialog>
    </>
  )
}

export default SettingPage
